import {
  WatchlistSeverityLevel,
  VisitorWatchlistMatchInfo,
} from 'lane-shared/domains/visitorManagement/types/VisitorPass';
import { ChipStyle } from 'components/ads';
import type { TFunction } from 'i18next';

const SEVERITY_PRIORITY: Record<string, number> = {
  [WatchlistSeverityLevel.WATCHLIST_SEVERITY_LEVEL_HIGH]: 3,
  [WatchlistSeverityLevel.WATCHLIST_SEVERITY_LEVEL_MEDIUM]: 2,
  [WatchlistSeverityLevel.WATCHLIST_SEVERITY_LEVEL_LOW]: 1,
  [WatchlistSeverityLevel.WATCHLIST_SEVERITY_LEVEL_INVALID]: 0,
  [WatchlistSeverityLevel.UNRECOGNIZED]: 0,
};

export function getHighestWatchlistSeverityLevel(
  matchInfo: VisitorWatchlistMatchInfo[]
): WatchlistSeverityLevel {
  return matchInfo.reduce<WatchlistSeverityLevel>(
    (highest, match) => {
      const currentPriority = SEVERITY_PRIORITY[match.watchlistSeverityLevel] ?? 0;
      const highestPriority = SEVERITY_PRIORITY[highest] ?? 0;

      return currentPriority > highestPriority
        ? match.watchlistSeverityLevel
        : highest;
    },
    WatchlistSeverityLevel.WATCHLIST_SEVERITY_LEVEL_INVALID
  );
}

export function getWatchlistSeverityTranslation(
  severityLevel: WatchlistSeverityLevel | string | undefined,
  t: TFunction
): string {
  const severityMap: Record<string, string> = {
    WATCHLIST_SEVERITY_LEVEL_INVALID:
      'web.admin.channel.visitor.watchlist.noMatches',
    WATCHLIST_SEVERITY_LEVEL_HIGH:
      'web.admin.channel.visitor.watchlist.highRisk',
    WATCHLIST_SEVERITY_LEVEL_MEDIUM:
      'web.admin.channel.visitor.watchlist.mediumRisk',
    WATCHLIST_SEVERITY_LEVEL_LOW:
      'web.admin.channel.visitor.watchlist.lowRisk',
  };

  const translationKey =
    severityMap[severityLevel || ''] ||
    severityMap.WATCHLIST_SEVERITY_LEVEL_INVALID;

  return t(translationKey);
}


export function isValidWatchlistSeverityLevel(
  severityLevel: WatchlistSeverityLevel | string | undefined
): boolean {
  if (!severityLevel) {
    return false;
  }

  const validSeverityLevels = [
    WatchlistSeverityLevel.WATCHLIST_SEVERITY_LEVEL_LOW,
    WatchlistSeverityLevel.WATCHLIST_SEVERITY_LEVEL_MEDIUM,
    WatchlistSeverityLevel.WATCHLIST_SEVERITY_LEVEL_HIGH,
  ];

  return validSeverityLevels.includes(severityLevel as WatchlistSeverityLevel);
}

export function getWatchlistSeverityChipStyle(
  severityLevel: WatchlistSeverityLevel | string | undefined
): ChipStyle {
  switch (severityLevel) {
    case WatchlistSeverityLevel.WATCHLIST_SEVERITY_LEVEL_HIGH:
      return ChipStyle.Red;
    case WatchlistSeverityLevel.WATCHLIST_SEVERITY_LEVEL_MEDIUM:
      return ChipStyle.Yellow;
    case WatchlistSeverityLevel.WATCHLIST_SEVERITY_LEVEL_LOW:
      return ChipStyle.Grey;
    default:
      return ChipStyle.Grey;
  }
}


export function isNoMatchesSeverity(
  severityLevel: WatchlistSeverityLevel | string | undefined
): boolean {
  if (!severityLevel) {
    return true;
  }

  const recognizedSeverityLevels: string[] = [
    WatchlistSeverityLevel.WATCHLIST_SEVERITY_LEVEL_HIGH,
    WatchlistSeverityLevel.WATCHLIST_SEVERITY_LEVEL_MEDIUM,
    WatchlistSeverityLevel.WATCHLIST_SEVERITY_LEVEL_LOW,
  ];

  return !recognizedSeverityLevels.includes(severityLevel);
}

